'use client';

import { useEffect, useRef } from 'react';
import { RegionCode } from '@peerless/ui-locale';
import { AuthenticationInteropService, mapUserInfo } from './AuthenticationService';
import { CurrentUserAttributes } from '@peerless/ui-amplify';

/**
 * Iframe Messaging Sequence:
 *
 * ┌─────────────┐          ┌───────┐          ┌─────────────┐
 * │ Parent      │          │ Auth  │          │ Iframe      │
 * │ Window      │          │ Svc   │          │ Configurator│
 * └─────┬───────┘          └───┬───┘          └──────┬──────┘
 *       │                      │                     │
 *       │ Create iframe        │                     │
 *       ├─────────────────────────────────────────> │
 *       │                      │                     │
 *       │                      │                     │ iframe.onload
 *       │ <─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─│
 *       │                      │                     │
 *       │ Send current user    │                     │
 *       │ ────────────────────────────────────────> │
 *       │                      │                     │
 *       │                      │     login_check     │
 *       │ <───────────────────────────────────────── │
 *       │                      │                     │
 *       │ auth.authenticate()  │                     │
 *       │ ─────────────────────>                     │
 *       │                      │                     │
 *       │                  user info                 │
 *       │ <─────────────────────                     │
 *       │                      │                     │
 *       │ Send user info       │                     │
 *       │ ────────────────────────────────────────> │
 *       │                      │                     │
 * ┌─────┴───────┐          ┌───┴───┐          ┌──────┴──────┐
 * │ Parent      │          │ Auth  │          │ Iframe      │
 * │ Window      │          │ Svc   │          │ Configurator│
 * └─────────────┘          └───────┘          └─────────────┘
 */

const IFRAME_DOMAIN = 'https://configurator.peerless-av.com';

export interface ModularSeriesConfiguratorProps {
  auth: AuthenticationInteropService;
  language: string;
  region: RegionCode;
  configId?: string;
}
const localeMap: Record<string, string> = {
  en: 'en',
  de: 'de',
  es: 'sp',
  fr: 'fr',
};

const siteMap: Record<RegionCode, string> = {
  US: 'US',
  MX: 'LA',
  UK: 'UK',
  FR: 'FR',
  DE: 'DE',
};

export function ModularSeriesConfigurator({
  auth,
  language,
  region,
  configId,
}: ModularSeriesConfiguratorProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check for container
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Create iframe element
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';

    function sendUserInfoToIframe(user: CurrentUserAttributes | undefined) {
      if (iframe.contentWindow) {
        const userData = mapUserInfo(user);
        iframe.contentWindow.postMessage(JSON.stringify(userData), IFRAME_DOMAIN);
        console.log('Data sent to iframe:', userData);
      }
    }

    // Handle messages from the iframe
    function handleIframeMessage(event: MessageEvent) {
      if (event.origin !== IFRAME_DOMAIN) return;

      try {
        // For string messages, parse them
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // Handle login_check message
        if (data.message === 'login_check') {
          console.log('Authentication requested from iframe');
          auth.authenticate(sendUserInfoToIframe);
        }
      } catch (error) {
        console.error('Error processing message from iframe:', error);
      }
    }

    // Build the iframe URL with all necessary parameters
    function buildIframeUrl() {
      const baseUrl = IFRAME_DOMAIN;
      const lang = localeMap[language] || 'en';
      const site = siteMap[region] || 'US';

      let url = `${baseUrl}/modular-series-configurator?lang=${lang}&site=${site}`;
      if (configId) {
        url += `&config_id=${configId}`;
      }

      return url;
    }

    // Add message event listener
    window.addEventListener('message', handleIframeMessage);

    // Send user data to iframe when it loads
    iframe.onload = () => {
      sendUserInfoToIframe(auth.user);
    };

    // Set the source
    iframe.src = buildIframeUrl();

    // Append iframe to container
    container.appendChild(iframe);

    return () => {
      // Clean up event listener
      window.removeEventListener('message', handleIframeMessage);

      // Remove iframe
      if (container && iframe) {
        container.removeChild(iframe);
      }
    };
  }, [auth, language, region, configId]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100vh',
        position: 'absolute',
        overflow: 'hidden',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 4000,
      }}
    />
  );
}
